import dayjs from "dayjs";

import { components } from "api/generated";
import { Button } from "atoms/button/Button";
import CameraFlip from "atoms/icon/icons/camera_flip.svg";
import Close from "atoms/icon/icons/close.svg";
import Billing from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import { InfoBox } from "atoms/info-box/InfoBox";
import { TextButton } from "atoms/text-button/TextButton";
import { SettingsContainer } from "molecules/containers/settings-container/SettingsContainer";
import { SettingsContainerButton } from "molecules/containers/settings-container-button/SettingsContainerButton";
import { getDunningDays } from "pages/root/wallet/Memberships/membershipHelper";
import { IfFlag, useFeatureFlag } from "services/Flags";
import {
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";
import pluralize from "utils/pluralize";

interface ManageMembershipCardProps {
  disableCancellationAllowed: boolean;
  membership: components["schemas"]["PlatformUserMembershipModel"];
  paymentMethodLast4?: string | null;
  onKeepMembership: () => void;
  onUpdatePaymentMethod: () => void;
  onReactivateMembership: () => void;
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"];
  onRetryMembershipPayment: (platformUserMembershipId: string) => void;
}

export enum MembershipStatus {
  active = "active",
  willCancel = "willCancel",
  canceled = "canceled",
  notAvailable = "notAvailable",
  dunning = "dunning",
}

export const getMembershipStatus = (
  membershipStatus: components["schemas"]["GetPlatformUserMembershipsStatusResult"],
  membershipActive: boolean
): MembershipStatus => {
  const willBeCanceled = !!membershipStatus.willCancelAt;
  const isCanceled = membershipStatus.canceled;
  const isNotAvailable = isCanceled && membershipActive === false;
  const isInDunning =
    membershipStatus.dunningStage && membershipStatus.enteredDunningAt;

  let status: MembershipStatus;

  if (isNotAvailable) {
    status = MembershipStatus.notAvailable;
  } else if (isCanceled) {
    status = MembershipStatus.canceled;
  } else if (isInDunning) {
    status = MembershipStatus.dunning;
  } else if (willBeCanceled) {
    status = MembershipStatus.willCancel;
  } else {
    status = MembershipStatus.active;
  }
  return status;
};

export const ManageMembershipCard = ({
  disableCancellationAllowed,
  membership,
  paymentMethodLast4,
  onUpdatePaymentMethod,
  onKeepMembership,
  onReactivateMembership,
  membershipStatus,
  onRetryMembershipPayment,
}: ManageMembershipCardProps) => {
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );

  const {
    active,
    billingPeriodInterval,
    billingPeriodIntervalCount,
    commitment = 0,
    id,
    name,
  } = membership.membership;

  const baseBillingPeriodInterval = parseBillingPeriodName(
    billingPeriodInterval
  );
  const adjustedCommitment = Math.max(commitment - 1, 0);

  const billingPeriodIntervalName = isFlexibleMembershipsEnabled
    ? getBillingIntervalBase(baseBillingPeriodInterval)
    : "month";

  const totalCommitmentPeriod =
    adjustedCommitment *
    (isFlexibleMembershipsEnabled ? billingPeriodIntervalCount : 1);

  const commitmentExpiration = dayjs(membership.created).add(
    totalCommitmentPeriod,
    billingPeriodIntervalName as dayjs.ManipulateType
  );

  const hasCommitmentPeriod = dayjs().isBefore(commitmentExpiration);
  const status = getMembershipStatus(membershipStatus, active);
  const numberOfAttempts = 4 - membership.dunningStage;
  const addedDunningDaysForNextPayment = getDunningDays(
    membership.dunningStage
  );
  const addedDunningDaysForLastPayment = getDunningDays(
    membership.dunningStage - 1
  );

  const lastManualPaymentAttemptDay = dayjs(
    membership.manualPaymentLastAttemptAt
  );
  const lastAutomaticPaymentAttemptDay = dayjs(membership.enteredDunningAt).add(
    addedDunningDaysForLastPayment,
    "day"
  );

  return (
    <div className="flex flex-col rounded-lg bg-white shadow-[0px_2px_10px_rgba(0,0,0,0.06)]">
      <div className="flex flex-col gap-y-4">
        <h3 className="flex justify-center pt-5 text-title">{name}</h3>
        {status == MembershipStatus.notAvailable && (
          <div className="flex flex-col gap-y-5 p-5 pt-0">
            <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-red">
              Membership canceled on{" "}
              {dayjs(membership.canceledDate).format("MMM D, YYYY")}
            </div>
            <InfoBox text="Membership is no longer available" />
          </div>
        )}
        {status == MembershipStatus.canceled && (
          <div className="flex flex-col gap-y-5 pb-5">
            <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-red">
              Membership canceled on{" "}
              {dayjs(membership.canceledDate).format("MMM D, YYYY")}
            </div>
            <div className="flex justify-center">
              <Button size="small" onClick={onReactivateMembership}>
                Reactivate
              </Button>
            </div>
          </div>
        )}
        {status == MembershipStatus.willCancel && (
          <div className="flex flex-col gap-y-3 p-4 pt-0">
            <InfoBox
              text={`Canceling on ${dayjs(membership.willCancelAt).format(
                "MMM D, YYYY"
              )}`}
              type="error"
            />
            <div className="flex justify-center">
              <TextButton onClick={onKeepMembership}>
                Keep my membership
              </TextButton>
            </div>
          </div>
        )}
        {(status == MembershipStatus.active ||
          status == MembershipStatus.dunning) && (
          <div className="flex flex-col gap-y-5">
            {status == MembershipStatus.dunning ? (
              <div className="flex flex-col items-center justify-center gap-y-3 px-3">
                <div className="text-bold1 text-red">
                  Your membership benefits have been disabled due to failed
                  payment.
                </div>
                <div className="text-body2 text-red">
                  We were unable to process your payment method{" "}
                  {paymentMethodLast4 && `ending in ${paymentMethodLast4} `}
                  on{" "}
                  {lastManualPaymentAttemptDay >= lastAutomaticPaymentAttemptDay
                    ? lastManualPaymentAttemptDay.format("MMM D, YYYY")
                    : lastAutomaticPaymentAttemptDay.format("MMM D, YYYY")}
                  .
                </div>
                <div className="text-body2 text-red">
                  We will try again automatically on{" "}
                  {dayjs(membership.enteredDunningAt)
                    .add(addedDunningDaysForNextPayment, "day")
                    .format("MMM D, YYYY")}
                  .
                </div>
                <div className="text-body2 text-red">
                  You have{" "}
                  <strong>
                    {numberOfAttempts} payment{" "}
                    {pluralize(numberOfAttempts, "attempt", "attempts")}{" "}
                  </strong>
                  remaining until your membership is automatically canceled and
                  you lose access to your membership benefits.
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-y-1 px-3 text-body2 text-secondary">
                {hasCommitmentPeriod && (
                  <span>
                    Minimum commitment: {commitmentExpiration.fromNow(true)}{" "}
                    remaining
                  </span>
                )}
                <span>
                  Next payment on{" "}
                  {dayjs(membership.expiration).format("MMM D, YYYY")}
                  {membership.paymentMethodDetails && (
                    <>
                      <br />
                      using payment method ending in{" "}
                      {membership.paymentMethodDetails.last4}
                    </>
                  )}
                </span>
              </div>
            )}
            <div className="border-black-600 overflow-clip rounded-b-lg border-t">
              {status == MembershipStatus.dunning && (
                <SettingsContainerButton
                  startIconSVG={CameraFlip}
                  onClick={() =>
                    onRetryMembershipPayment(
                      membership.platformUserMembershipId
                    )
                  }
                >
                  Retry current payment method
                </SettingsContainerButton>
              )}
              <SettingsContainerButton
                startIconSVG={Billing}
                onClick={onUpdatePaymentMethod}
              >
                Update payment method
              </SettingsContainerButton>
              <SettingsContainer
                disabled={hasCommitmentPeriod && disableCancellationAllowed}
                to={`/wallet/memberships/manage/${id}${
                  status === "dunning" ? "/dunning" : ""
                }`}
                startIconSVG={Close}
              >
                Cancel membership
              </SettingsContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
